import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { X, ChevronDown, Plus } from 'lucide-react';
import * as Select from '@radix-ui/react-select';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '../../../components/ui/button/Button';
import { SearchBar, SearchBarOption } from '../../../components/ui/search/Searchbar';
import { cn, getInitials } from 'src/lib/utils';
import PrimaryActionButton from '../../../components/ui/button/PrimaryActionButton';
import { ReviewerItem } from './ReviewerItem';
import { ReportReviewPayload, ReviewStatusEnum, UserActionType } from 'src/api/UserActionsStatus';
import { getDefaultReviewStatus } from 'src/features/userActionsStatus/logic/userActionsStatusSlice';
import { reviewerLevelDisplay } from 'src/features/userActionsStatus/utils/reviewerMappers';
import ButtonV2 from '../../../components/ui/button/v2';
import { SaveButtonV2 } from '../../../components/ui/button/v2/SaveButton';

type ReviewerUser = {
    email_id: string;
    user_name: string;
};

export interface ReviewerManagerDialogProps {
    isOpen: boolean;
    onClose: () => void;
    initialReviewers: ReportReviewPayload[];
    availableReviewers: ReviewerUser[];
    onSave: (updatedReviewers: ReportReviewPayload[]) => void;
    isSaving: boolean;
    currentUserEmail: string;
    title?: string;
    levels: UserActionType[];
    showStatusIcon?: boolean;
}

export const ReviewerManagerDialog: React.FC<ReviewerManagerDialogProps> = ({
    isOpen,
    onClose,
    initialReviewers,
    availableReviewers,
    onSave,
    isSaving,
    currentUserEmail,
    title = 'Manage Reviewers',
    levels,
    showStatusIcon = true,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [newReviewers, setNewReviewers] = useState<ReportReviewPayload[]>([]);

    // This effect resets the session's changes whenever the dialog is opened.
    useEffect(() => {
        if (isOpen) {
            setNewReviewers([]);
        }
    }, [isOpen]);

    const searchOptions: SearchBarOption[] = useMemo(() => {
        return availableReviewers.map((u) => ({ id: u.email_id, label: u.user_name, value: u }));
    }, [availableReviewers]);

    const mergedReviewers = useMemo(() => {
        const reviewersMap = new Map<UserActionType, ReportReviewPayload>();
        initialReviewers.forEach(r => reviewersMap.set(r.action_type!, r));
        newReviewers.forEach(r => reviewersMap.set(r.action_type!, r));
        return Array.from(reviewersMap.values());
    }, [initialReviewers, newReviewers]);

    const addedUserEmails = useMemo(() => new Set(mergedReviewers.filter(r => r.review_status.approver).map((r) => r.review_status.approver as string)), [mergedReviewers]);
    const addedLevels = useMemo(() => new Set(mergedReviewers.filter(r => r.review_status.approver).map(r => r.action_type)), [mergedReviewers]);

    const initialSelectedLevel: UserActionType = useMemo(() => (levels.find(lvl => !addedLevels.has(lvl)) ?? levels[0]), [addedLevels, levels]);
    const [selectedLevel, setSelectedLevel] = useState<UserActionType>(initialSelectedLevel);

    useEffect(() => {
        setSelectedLevel(initialSelectedLevel);
    }, [initialSelectedLevel]);

    const handleSave = useCallback(() => {
        onSave(newReviewers);
    }, [onSave, newReviewers]);

    const handleCancel = useCallback(() => {
        setSearchQuery('');
        onClose();
    }, [onClose]);

    const filteredOptions = useMemo(() => {
        if (!searchQuery.trim()) return searchOptions;
        const query = searchQuery.toLowerCase();
        return searchOptions.filter((o) => o.label.toLowerCase().includes(query));
    }, [searchOptions, searchQuery]);

    const highlightText = useCallback((text: string, query: string): React.ReactNode => {
        if (!query.trim()) return text;
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
        return parts.map((part, index) =>
            part.toLowerCase() === query.toLowerCase() ? (
                <span key={index} className="font-bold">{part}</span>
            ) : (
                part
            )
        );
    }, []);

    const renderReviewerOption = useCallback((opt: SearchBarOption) => {
        const user = opt.value as ReviewerUser;
        const isAlreadyAdded = user && addedUserEmails.has(user.email_id);
        return (
            <div className={cn("flex items-center gap-3 w-full", isAlreadyAdded && "opacity-50 cursor-not-allowed")}>
                <div className="h-5 w-5 rounded-full bg-[#f5f5f5] flex items-center justify-center text-[10px] font-medium text-gray-700">{user ? getInitials(user.user_name) : ''}</div>
                <div className="flex-1 text-sm">{user ? highlightText(user.user_name, searchQuery) : ''}</div>
                {isAlreadyAdded && (<span className="text-sm text-muted-foreground">Already Added</span>)}
            </div>
        );
    }, [addedUserEmails, highlightText, searchQuery]);

    const handleAddReviewer = useCallback(() => {
        const selectedOpt = searchOptions.find((o) => o.id === searchQuery);
        if (!selectedOpt || addedLevels.has(selectedLevel)) return;

        const user = selectedOpt.value as ReviewerUser;
        if (user && !addedUserEmails.has(user.email_id)) {
            const newReviewer = {
                review_status: {
                    ...getDefaultReviewStatus(),
                    level: selectedLevel,
                    approver: user.email_id,
                    assigned_by: currentUserEmail,
                    status: ReviewStatusEnum.ASSIGNED,
                },
                action_type: selectedLevel,
            };
            setNewReviewers((prev) => [
                ...prev.filter((r) => r.action_type !== selectedLevel),
                newReviewer,
            ]);
            setSearchQuery('');
        }
    }, [searchQuery, searchOptions, addedLevels, selectedLevel, addedUserEmails, currentUserEmail]);


    const handleRemoveReviewer = useCallback((levelToRemove: UserActionType) => {
        const defaultState = { review_status: { ...getDefaultReviewStatus(), level: levelToRemove }, action_type: levelToRemove };
        setNewReviewers(prev => [...prev.filter(r => r.action_type !== levelToRemove), defaultState]);
    }, []);

    const initialApprovers = useMemo(() => new Set(initialReviewers.filter(r => r.review_status.approver).map(r => r.review_status.approver)), [initialReviewers]);
    const canRemoveReviewer = (reviewer: ReportReviewPayload) => !!reviewer.review_status.approver && !initialApprovers.has(reviewer.review_status.approver);
    const reviewerNameMap = useMemo(() => availableReviewers.reduce((acc, user) => { acc[user.email_id] = user.user_name; return acc; }, {} as Record<string, string>), [availableReviewers]);
    const isLevelAlreadySelected = addedLevels.has(selectedLevel);

    const selectedOpt = searchOptions.find((o) => o.id === searchQuery);
    const isAddButtonDisabled = (!selectedOpt && (!searchQuery.trim() || filteredOptions.length === 0)) || isLevelAlreadySelected;
    const addButtonTitle = isLevelAlreadySelected ? 'Reviewer for this level has already been added' : 'Add reviewer';
    const assignedReviewers = mergedReviewers.filter(r => r.review_status.approver);

    return (
        <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[9998]" />
                <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[95vw] max-w-2xl max-h-[80vh] bg-white rounded-lg shadow-lg focus:outline-none">
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <Dialog.Title className="text-xl font-semibold text-gray-900">{title}</Dialog.Title>
                        <Dialog.Close asChild>
                            <button onClick={handleCancel} disabled={isSaving} className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                <X className="w-5 h-5" />
                            </button>
                        </Dialog.Close>
                    </div>

                    <div className="p-6 pt-4">
                        <div className="flex gap-3 mb-4">
                            <div className="flex-1">
                                <SearchBar
                                    placeholder="Add New Reviewer"
                                    value={
                                        // if query is an email, map to the display name for UI
                                        searchOptions.find((o) => o.id === searchQuery)?.label ?? searchQuery
                                    }
                                    onChange={setSearchQuery}
                                    options={filteredOptions}
                                    onSelect={(option) => setSearchQuery(option.id)}
                                    isOptionDisabled={(opt) => addedUserEmails.has((opt.value as ReviewerUser).email_id)}
                                    renderOption={renderReviewerOption}
                                />
                            </div>

                            <Select.Root value={selectedLevel} onValueChange={(val) => setSelectedLevel(val as UserActionType)}>
                                <Select.Trigger disabled={isSaving} className="w-40 h-[38px] inline-flex items-center justify-between border rounded-md px-3 text-sm bg-white text-gray-900">
                                    <Select.Value />
                                    <Select.Icon><ChevronDown className="h-4 w-4 text-gray-500" /></Select.Icon>
                                </Select.Trigger>
                                <Select.Portal>
                                    <Select.Content className="overflow-hidden bg-white border rounded-md shadow-lg z-[10000]">
                                        <Select.Viewport className="p-1">
                                            {levels.map((lvl) => (
                                                <Select.Item key={lvl} value={lvl} disabled={addedLevels.has(lvl)} className="px-3 py-2 text-sm rounded-md cursor-pointer select-none data-[highlighted]:bg-gray-100 data-[disabled]:opacity-50">
                                                    <Select.ItemText>{reviewerLevelDisplay[lvl]}</Select.ItemText>
                                                </Select.Item>
                                            ))}
                                        </Select.Viewport>
                                    </Select.Content>
                                </Select.Portal>
                            </Select.Root>

                            <PrimaryActionButton
                                text="Add"
                                icon={<Plus />}
                                disabled={isAddButtonDisabled || isSaving}
                                variant="gradient" className="px-6"
                                onClick={handleAddReviewer}
                                title={addButtonTitle}
                            />
                        </div>

                        <div className="max-h-[44vh] overflow-y-auto space-y-2">
                            {assignedReviewers.map((reviewer) => (<ReviewerItem
                                key={reviewer.action_type}
                                reviewer={reviewer}
                                reviewerName={reviewerNameMap[reviewer.review_status.approver!]}
                                showStatusIcon={showStatusIcon}
                                onRemove={() => handleRemoveReviewer(reviewer.action_type!)}
                                canRemove={canRemoveReviewer(reviewer)}
                                isLoading={isSaving}
                            />
                            )
                            )}
                        </div>

                        {assignedReviewers.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                                No Reviewers Added.
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
                        <ButtonV2 variant="ghost" size="md" onClick={handleCancel} disabled={isSaving}>
                            Cancel
                        </ButtonV2>
                        <SaveButtonV2
                            className={cn('bg-blue-600 hover:bg-blue-700 text-white')}
                            onClick={handleSave}
                            buttonText={isSaving ? 'Saving...' : 'Save'}
                            disabled={isSaving}
                            showLeftIcon={false}
                        />
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};